import TramiteTypesAPI from 'dashboard/api/tramiteTypes';

// Vuex module for the tramite-types service catalog (settings). Type-level records are cached here;
// nested stages are fetched on demand by the editor (returned to the caller, not cached) since they
// are only edited inside a single open type. Mirrors the inline-mutation style of the tramites module.
const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

const getters = {
  getTramiteTypes: $state => $state.records,
  getUIFlags: $state => $state.uiFlags,
  getTramiteTypeById: $state => id =>
    $state.records.find(type => Number(type.id) === Number(id)),
};

const actions = {
  async fetchTramiteTypes({ commit }, params = {}) {
    commit('setUIFlag', { isFetching: true });
    try {
      const response = await TramiteTypesAPI.get(params);
      commit('setTramiteTypes', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },

  async fetchTramiteType({ commit }, id) {
    const response = await TramiteTypesAPI.show(id);
    commit('addOrUpdateTramiteType', response.data);
    return response.data;
  },

  async createTramiteType({ commit }, data) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await TramiteTypesAPI.create(data);
      commit('addOrUpdateTramiteType', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async updateTramiteType({ commit }, { id, ...data }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await TramiteTypesAPI.update(id, data);
      commit('addOrUpdateTramiteType', response.data);
      return response.data;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async deleteTramiteType({ commit }, id) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await TramiteTypesAPI.delete(id);
      commit('removeTramiteType', id);
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  // --- Nested stages (not cached; returned to the editor) ---
  async fetchStages(_, typeId) {
    const response = await TramiteTypesAPI.getStages(typeId);
    return response.data;
  },

  async createStage(_, { typeId, ...data }) {
    const response = await TramiteTypesAPI.createStage(typeId, data);
    return response.data;
  },

  async updateStage(_, { typeId, stageId, ...data }) {
    const response = await TramiteTypesAPI.updateStage(typeId, stageId, data);
    return response.data;
  },

  async deleteStage(_, { typeId, stageId }) {
    await TramiteTypesAPI.deleteStage(typeId, stageId);
  },

  async reorderStages(_, { typeId, ids }) {
    const response = await TramiteTypesAPI.reorderStages(typeId, ids);
    return response.data;
  },

  // --- Nested forms (not cached; returned to the editor) ---
  async fetchForms(_, typeId) {
    const response = await TramiteTypesAPI.getForms(typeId);
    return response.data;
  },

  async createForm(_, { typeId, ...data }) {
    const response = await TramiteTypesAPI.createForm(typeId, data);
    return response.data;
  },

  async updateForm(_, { typeId, formId, ...data }) {
    const response = await TramiteTypesAPI.updateForm(typeId, formId, data);
    return response.data;
  },

  async deleteForm(_, { typeId, formId }) {
    await TramiteTypesAPI.deleteForm(typeId, formId);
  },

  async reorderForms(_, { typeId, ids }) {
    const response = await TramiteTypesAPI.reorderForms(typeId, ids);
    return response.data;
  },
};

const mutations = {
  setTramiteTypes($state, records) {
    $state.records = records || [];
  },
  addOrUpdateTramiteType($state, type) {
    if (!type || !type.id) return;
    const index = $state.records.findIndex(t => t.id === type.id);
    if (index !== -1) {
      $state.records.splice(index, 1, type);
    } else {
      $state.records.push(type);
    }
  },
  removeTramiteType($state, id) {
    $state.records = $state.records.filter(type => type.id !== id);
  },
  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
};

export default { namespaced: true, state, getters, actions, mutations };
